import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../lib/supabase.js";

const AuthContext = createContext(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/**
 * Session plus the signed-in user's `profiles` row.
 *
 * Both halves matter. Supabase Auth answers "who are you"; the profile answers
 * "what may you do" — every RLS policy in halco-schema.sql routes through
 * is_staff()/is_admin(), which read `profiles`.
 *
 * A user with no profile row is the important edge case. RLS filters rows
 * rather than raising, so that user authenticates fine and then sees an empty
 * app with no error anywhere. `profileMissing` exists so the UI can say what is
 * actually wrong instead of rendering blank tables.
 */
export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileMissing, setProfileMissing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session ?? null);
      if (!data.session) setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      if (!active) return;
      setSession(next);
      if (!next) {
        setProfile(null);
        setProfileMissing(false);
        setLoading(false);
      }
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!session?.user) return;
    let active = true;
    setLoading(true);
    setError(null);

    supabase
      .from("profiles")
      .select("id,name,role,perms")
      .eq("id", session.user.id)
      .maybeSingle()
      .then(({ data, error: err }) => {
        if (!active) return;
        if (err) {
          setError(err.message);
        } else if (!data) {
          // Authenticated but not authorised. Not an error condition to the
          // database — just an absent row.
          setProfileMissing(true);
        } else {
          setProfile(data);
          setProfileMissing(false);
        }
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [session?.user?.id]);

  const value = {
    session,
    user: session?.user ?? null,
    profile,
    profileMissing,
    loading,
    error,
    isAdmin: profile?.role === "admin",
    signIn: (email, password) => supabase.auth.signInWithPassword({ email, password }),
    signOut: () => supabase.auth.signOut(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
